import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export default function DashSettings() {
  const [s, setS] = React.useState(null);
  const { t, lang, setLang } = useLang();

  React.useEffect(() => {
    api.get("/api/dashboard/settings").then((r) => setS(r.data));
  }, []);

  const save = async () => {
    try {
      const payload = {
        webhook_url: s.webhook_url || null,
        ip_allowlist: (s.ip_allowlist || []),
        monthly_spend_limit: s.monthly_spend_limit ? Number(s.monthly_spend_limit) : null,
        usage_alerts_enabled: !!s.usage_alerts_enabled,
        language: s.language || "en",
      };
      const { data } = await api.put("/api/dashboard/settings", payload);
      setS(data);
      if (data.language && data.language !== lang) setLang(data.language);
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!s) return <div className="h-40 lp-shimmer rounded-2xl" />;

  return (
    <div className="space-y-6" data-testid="dash-settings-page">
      <div>
        <h1 className="font-display text-3xl text-white/95">{t("dash.settings.title")}</h1>
        <p className="text-white/50 text-sm mt-1">{t("dash.settings.subtitle")}</p>
      </div>

      <div className="lp-card p-6 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("dash.settings.webhook")}</label>
          <input className="lp-input" value={s.webhook_url || ""} onChange={(e) => setS({ ...s, webhook_url: e.target.value })} placeholder={t("dash.settings.webhook_placeholder")} data-testid="settings-webhook" />
          <p className="text-xs text-white/40 mt-2">{t("dash.settings.webhook_hint")}</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("dash.settings.allowlist")}</label>
          <textarea
            className="lp-input min-h-[80px]"
            placeholder={t("dash.settings.allowlist_placeholder")}
            value={(s.ip_allowlist || []).join("\n")}
            onChange={(e) => setS({ ...s, ip_allowlist: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
            data-testid="settings-allowlist"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("dash.settings.spend_limit")}</label>
            <input type="number" min={0} className="lp-input" value={s.monthly_spend_limit ?? ""} onChange={(e) => setS({ ...s, monthly_spend_limit: e.target.value })} placeholder="500" data-testid="settings-spend-limit" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("dash.settings.language")}</label>
            <select className="lp-input" value={s.language || "en"} onChange={(e) => setS({ ...s, language: e.target.value })} data-testid="settings-language">
              <option value="en">{t("dash.settings.lang_en")}</option>
              <option value="tr">{t("dash.settings.lang_tr")}</option>
            </select>
          </div>
        </div>
        <label className="flex items-center gap-3 pt-2">
          <input type="checkbox" checked={!!s.usage_alerts_enabled} onChange={(e) => setS({ ...s, usage_alerts_enabled: e.target.checked })} data-testid="settings-alerts" />
          <span className="text-white/70 text-sm">{t("dash.settings.alerts")}</span>
        </label>
        <div className="flex justify-end pt-2">
          <button onClick={save} className="lp-btn-primary" data-testid="settings-save">{t("dash.settings.save")}</button>
        </div>
      </div>
    </div>
  );
}
